import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Pagination,
  Tooltip,
  Typography
} from '@mui/material';
import palette from 'src/theme/palette';
import { TableNoData } from 'src/components/NoData';
import Iconify from 'src/components/Iconify';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { api } from 'src/Api';
import DeleteModal from 'src/components/DeleteModal';
import AddEditTaskModal from './AddEditTaskModal';
import TaskDetailDialog from './TaskDetailDialog';
import TaskNotesEditModal from './TaskNotesEditModal';

const TaskTable = ({ selectedData, permission }) => {
  const token = localStorage.getItem('token');

  const user = JSON.parse(localStorage.getItem('user'));
  const role_id = user?.role?.id;
  const isLimitedRole = [5, 6, 7].includes(role_id);

  const [list, setlist] = useState([]);
  const [loading, setloading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [showTaskModalNote, setShowTaskModalNote] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const refresh = () => {
    setlist([]);
    getSubProjectTaskData();
  };

  useEffect(() => {
    if (selectedData?.id) {
      list.length = 0;
      getSubProjectTaskData(pagination.current_page);
    }
  }, [pagination.current_page]);

  function getSubProjectTaskData(page) {
    setloading(true);
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    page && queryParams.append('page', page);
    selectedData?.id && queryParams.append('sub_project_id', selectedData?.id);
    // ✅ LocalStorage se saved filters nikal kar query me bhejna
    const filterKeys = [
      'task_code_ids',
      'task_name_ids',
      'task_description_ids',
      'task_category_ids',
      'task_sel_ids'
    ];

    filterKeys.forEach((key) => {
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null && storedValue !== 'undefined') {
        queryParams.append(key, storedValue);
      }
    });

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.get_sub_project_task}?${queryParams.toString()}`, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch Deal data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setlist(result?.data);
        setPagination(result.pagination);
      })
      .catch((error) => {
        console.log('error', error);
      })
      .finally(() => {
        setloading(false);
      });
  }

  const handleDeleteModal = (row) => {
    setSelectedProject(row);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedProject(null);
    setShowDeleteModal(false);
  };

  function handleDeleteSubProject() {
    setStatusLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    queryParams.append('id', selectedProject?.id);
    queryParams.append('sub_project_id', selectedData?.id);

    const requestOptions = {
      method: 'DELETE',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.delete_task}?${queryParams}`, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          handleCloseDeleteModal();
          toast.success(result.message);
          refresh();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setStatusLoading(false);
      });
  }

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, current_page: newPage }));
  };

  const handleDetailModalOpen = (row) => {
    setSelectedProject(row);
    setShowDetailModal(true);
  };

  const handleDetailModalClose = () => {
    setSelectedProject(null);
    setShowDetailModal(false);
  };

  const handleAddNotModalClose = () => {
    setEditingData(null);
    setShowTaskModalNote(false);
  };

  return (
    <Box sx={{ position: 'relative', ml: 4 }}>
      {/* 🔹 Left vertical line */}
      <Box
        sx={{
          position: 'absolute',
          top: -9,
          left: -14, // adjust according to spacing
          width: '3px',
          borderBottomRadius: 10,
          height: 31,
          bgcolor: palette.primary.main // light gray line
        }}
      />

      {/* 🔹 Horizontal connector from line to TaskTable */}
      <Box
        sx={{
          position: 'absolute',
          top: 20, // align with TaskTable header row
          left: -14,
          width: 16,
          height: '3.3px',
          borderRadius: 10,
          bgcolor: palette.primary.main
        }}
      />
      <TableContainer
        component={Paper}
        sx={{
          mt: 1,
          width: '97%',
          border: '1px solid #E5E7EB',
          borderRadius: 1,
          boxShadow: 'none', // remove heavy shadow
          backgroundColor: '#fafafa', // subtle bg difference
          position: 'relative',
          minHeight: 310,
          maxHeight: 400,
          mb: 2
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              minHeight: 200,
              background: 'rgba(255,255,255,0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        <Table size="small" stickyHeader sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell
                sx={{
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  width: '24%'
                }}
              >
                Task Name
              </TableCell>

              <TableCell
                sx={{
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 1,
                  width: '35%'
                }}
              >
                Description
              </TableCell>

              <TableCell
                sx={{
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin'
                }}
              >
                Unit
              </TableCell>
              {!isLimitedRole && (
                <TableCell
                  sx={{
                    fontSize: 11,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsThin'
                  }}
                >
                  Unit-Cost
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin'
                }}
              >
                Quantity
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontFamily: 'poppinsThin',
                  color: palette.primary.tableHeadTitle,
                  pl: 1,
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  backgroundColor: '#fff'
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          {!loading && (
            <TableBody>
              {list.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f9fafb'
                  }}
                >
                  {/* SUB PROJECT NAME */}
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsRegular',
                        color: palette.primary.main,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '170px',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: palette.primary.mainDark
                        }
                      }}
                      onClick={() => handleDetailModalOpen(row)}
                    >
                      {row?.name || 'N/A'}
                    </Typography>
                  </TableCell>

                  {/* Description */}
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontFamily: 'poppinsMedium',
                      pl: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {row?.description || 'N/A'}
                  </TableCell>

                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        pl: 0
                      }}
                    >
                      {row?.unit_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  {!isLimitedRole && (
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium',
                          pl: 0
                        }}
                      >
                        {row?.unit_cost || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'poppinsMedium',
                        pl: 0
                      }}
                    >
                      {row?.qty || 'N/A'}
                    </Typography>
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontFamily: 'poppinsMedium',
                      pl: 1,
                      position: {
                        xs: 'static', // small
                        sm: 'static', // medium
                        md: 'static', // medium
                        lg: 'sticky' // large+
                      },
                      right: { lg: 0 },
                      zIndex: { lg: 2 },
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5 // proper spacing between buttons
                    }}
                  >
                    {permission.includes('edit task') && (
                      <Tooltip title="Edit">
                        <LoadingButton
                          size="small"
                          variant="contained"
                          sx={{
                            justifyContent: 'center',
                            width: 25,
                            height: 25,
                            minWidth: 25,
                            p: 0,
                            borderRadius: 0.7
                          }}
                          onClick={() => {
                            setShowTaskModal(true);
                            setEditingData(row);
                          }}
                        >
                          <Iconify
                            sx={{ cursor: 'pointer', fontSize: 13, color: '#ffffff' }}
                            icon="cuida:edit-outline"
                          />
                        </LoadingButton>
                      </Tooltip>
                    )}

                    {permission.includes('delete task') && (
                      <Tooltip title="Delete">
                        <LoadingButton
                          size="small"
                          variant="contained"
                          sx={{
                            justifyContent: 'center',
                            width: 25,
                            height: 25,
                            minWidth: 25,
                            p: 0,
                            borderRadius: 0.7,
                            backgroundColor: '#f72929ff',
                            ':hover': { backgroundColor: '#cb2020ff' }
                          }}
                          onClick={() => handleDeleteModal(row)}
                        >
                          <Iconify
                            sx={{ cursor: 'pointer', fontSize: 16, color: '#ffffff' }}
                            icon="material-symbols-light:delete-rounded"
                          />
                        </LoadingButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Update Quantity & Note">
                      <LoadingButton
                        size="small"
                        variant="contained"
                        sx={{
                          justifyContent: 'center',
                          width: 25,
                          height: 25,
                          minWidth: 25,
                          p: 0,
                          borderRadius: 0.7
                        }}
                        onClick={() => {
                          setShowTaskModalNote(true);
                          setEditingData(row);
                        }}
                      >
                        <Iconify
                          sx={{ cursor: 'pointer', fontSize: 17, color: '#ffffff' }}
                          icon="proicons:note"
                        />
                      </LoadingButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}

          {/* Empty State */}
          {list?.length === 0 && !loading && <TableNoData colSpan={16} />}
        </Table>
      </TableContainer>
      {/* PAGINATION */}
      {!loading && pagination.last_page > 1 && list.length !== 0 && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
          mb={1}
          mt={-1}
          px={4}
        >
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* DETAIL MODAL */}
      <TaskDetailDialog
        open={showDetailModal}
        onClose={handleDetailModalClose}
        task={selectedProject}
      />

      {/* DETAIL MODAL */}
      <TaskNotesEditModal
        open={showTaskModalNote}
        onClose={handleAddNotModalClose}
        refresh={refresh}
        initialData={editingData}
      />

      {/* ADD TASK MODAL */}
      <AddEditTaskModal
        open={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setEditingData(null);
        }}
        refresh={refresh}
        selectedTaskData={editingData}
        sub_project_id={selectedData?.id}
        project_id={selectedData?.project_id}
      />

      {/* DELETE SUB PROJECT MODAL */}
      <DeleteModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteSubProject}
        loading={statusLoading}
        title="Delete Task"
        description={`Are you sure you want to delete the task "${selectedProject?.name}" under the sub-project "${selectedData?.name}"?`}
      />
    </Box>
  );
};

export default TaskTable;
